import { extractErrorMessage } from "../utils/acceptSuggestions";
/**
 * ProfileUpdatePage — V22.9.5 · Universal Asset Profile Gate.
 *
 * Renders as a full-screen TurboTax-style wizard via <StepDialog/>. Driven by
 * the role-aware blueprint returned from GET /api/profile-update/template.
 *
 * Roles routed here:
 *   - process_manager  → Business Process Profile  (4 steps)
 *   - app_manager      → Business Application Profile (4 steps)
 *   - it_function_manager → IT Service Profile (4 steps)
 *   - all contributor / facility / vendor variants
 *
 * Exempt (admin / bcm_manager / department_head) — bounced straight to Mission Control.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import StepDialog from "../components/StepDialog";
import AssetPicker from "../components/AssetPicker";
import AssetProfileCopilot from "../components/AssetProfileCopilot";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { ClipboardList, MapPin, Gauge, Users, Server, ShieldCheck, Network, Building2, HelpCircle, Plus, Trash2, Cable, FileText } from "lucide-react";

// V22.9.8 — Inline help bubble (deterministic glossary, zero LLM cost)
const HelpTip = ({ term, glossary }) => {
  const entry = glossary?.[term];
  if (!entry) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex" onClick={(e) => e.stopPropagation()}>
          <HelpCircle className="w-3 h-3 text-violet-500 hover:text-violet-700" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 text-[12px]" align="start" data-testid={`glossary-tip-${term}`}>
        <p className="font-semibold text-violet-700 mb-1">{entry.label}</p>
        <p className="text-slate-600 leading-snug">{entry.explanation}</p>
        <p className="text-[10px] text-slate-400 mt-2 italic">V22.9.8 · zero-cost deterministic glossary</p>
      </PopoverContent>
    </Popover>
  );
};

// V22.9.8 — Free-form Connectivity rows editor (Step 5 of App Profile)
const ConnectivityRowsEditor = ({ rows, onChange, glossary }) => {
  const update = (idx, patch) => onChange(rows.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  const remove = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const add = () => onChange([...rows, { protocol: "API", vendor_name: "", source_ip: "", destination_ip: "", port: "" }]);
  return (
    <div className="space-y-2" data-testid="connectivity-rows-editor">
      <div className="flex items-center justify-between">
        <Label className="text-xs flex items-center gap-1">
          <Cable className="w-3 h-3 text-violet-500" />
          Connectivity & Protocols
          <HelpTip term="connectivity_rows" glossary={glossary} />
        </Label>
        <Button type="button" size="sm" variant="outline" onClick={add} className="h-7 text-[11px]" data-testid="connectivity-add-btn">
          <Plus className="w-3 h-3 mr-1" /> Add Row
        </Button>
      </div>
      {rows.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">No connectivity rows yet — add VPN / API / SFTP integrations that this app uses. They federate into the BIA dependency picker automatically.</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-1.5 items-center border rounded p-1.5 bg-slate-50/50" data-testid={`connectivity-row-${idx}`}>
              <Select value={r.protocol || "API"} onValueChange={(v) => update(idx, { protocol: v })}>
                <SelectTrigger className="col-span-2 h-7 text-[11px]"><SelectValue /></SelectTrigger>
                <SelectContent>{["VPN", "API", "SFTP", "MQ", "DB_LINK", "OTHER"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
              <Input className="col-span-3 h-7 text-[11px]" value={r.vendor_name || ""} onChange={(e) => update(idx, { vendor_name: e.target.value })} placeholder="Vendor / partner" />
              <Input className="col-span-2 h-7 text-[11px]" value={r.source_ip || ""} onChange={(e) => update(idx, { source_ip: e.target.value })} placeholder="Source IP" />
              <Input className="col-span-2 h-7 text-[11px]" value={r.destination_ip || ""} onChange={(e) => update(idx, { destination_ip: e.target.value })} placeholder="Dest IP" />
              <Input className="col-span-2 h-7 text-[11px]" value={r.port || ""} onChange={(e) => update(idx, { port: e.target.value })} placeholder="Port" />
              <button type="button" onClick={() => remove(idx)} className="col-span-1 text-slate-400 hover:text-red-600" data-testid={`connectivity-row-${idx}-remove`}>
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const STEP_ICON = {
  identity: ClipboardList,
  location: MapPin,
  criticality: Gauge,
  dependencies: Users,
  hosting: Server,
  compliance: ShieldCheck,
  scope: Network,
  people: Users,
  contact: Building2,
  // V22.9.8
  ownership: Users,
  network: Network,
  connectivity: Cable,
  physicality: Building2,
};

// Map flat form fields → typed top-level + `details` sub-object for submission.
const TOP_LEVEL_FIELDS = new Set([
  "asset_type", "asset_name", "description", "owner_name", "owner_email", "owner_phone",
  "primary_location", "current_address_line1", "city", "country",
  "business_criticality", "tier", "rto_hours", "rpo_hours",
]);

export default function ProfileUpdatePage() {
  const { user, api, updateUser } = useAuth();
  const navigate = useNavigate();
  const [template, setTemplate] = useState(null);
  const [glossary, setGlossary] = useState({});
  const [form, setForm] = useState({});
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tpl, gloss] = await Promise.all([
          api.get("/profile-update/template"),
          api.get("/profile-update/glossary").catch(() => ({ data: { terms: {} } })),
        ]);
        const data = tpl.data;
        setTemplate(data);
        setGlossary(gloss.data?.terms || {});
        if (data.exempt_admin) {
          // Admins / BCM Managers / Department Heads — straight to mission control
          navigate("/", { replace: true });
          return;
        }
        setForm({
          owner_email: data?.prefill?.owner_email || user?.email || "",
          owner_name: data?.prefill?.owner_name || (user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() : ""),
          owner_phone: data?.prefill?.owner_phone || "",
          department: data?.prefill?.department || "",
          asset_type: data?.blueprint?.asset_type || "other",
          business_criticality: "medium",
          tier: "tier_3",
          compliance_tags: [],
          deployment_regions: [],
          key_personnel: [],
          upstream_asset_ids: [],
          downstream_asset_ids: [],
          dns_names: [],
          firewall_zones: [],
          regulatory_scope: [],
          service_lines: [],
          connectivity_rows: [],
        });
      } catch (e) {
        console.error(e);
        toast.error("Failed to load profile template");
      }
    })();
  }, [api, user, navigate]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setListCSV = (k) => (e) => set(k, e.target.value.split(",").map(s => s.trim()).filter(Boolean));

  const stepsConfig = template?.blueprint?.steps || [];
  const currentStepDef = stepsConfig[step];

  // Per-step validation — at minimum the "identity" step needs asset_name + owner_name
  const canAdvance = useMemo(() => {
    if (!currentStepDef) return true;
    if (currentStepDef.id === "identity") return !!(form.asset_name && form.owner_name);
    // V22.9.8 — location is now folded into the relevant step (criticality / physicality /
    // compliance) rather than being its own step. We validate location at submit time only
    // for asset types that still surface those fields.
    return true;
  }, [currentStepDef, form]);

  const stepPills = stepsConfig.map((s, idx) => ({
    id: s.id,
    title: s.title,
    icon: STEP_ICON[s.id] || ClipboardList,
    isComplete: idx < step,
  }));

  // ── Submit handler ──
  const handleSubmit = async () => {
    if (!form.asset_name || !form.owner_name) {
      toast.error("Please complete the Identity step first");
      setStep(0); return;
    }
    // V22.9.8 — location/address are required only when surfaced by the active blueprint
    const allFields = stepsConfig.flatMap((s) => s.fields || []);
    if (allFields.includes("primary_location") && !form.primary_location) {
      toast.error("Primary Location is required");
      const idx = stepsConfig.findIndex((s) => (s.fields || []).includes("primary_location"));
      if (idx >= 0) setStep(idx);
      return;
    }
    if (allFields.includes("current_address_line1") && !form.current_address_line1) {
      toast.error("Address is required");
      const idx = stepsConfig.findIndex((s) => (s.fields || []).includes("current_address_line1"));
      if (idx >= 0) setStep(idx);
      return;
    }
    // Split form into top-level + details sub-object
    const topLevel = {}, details = {};
    Object.entries(form).forEach(([k, v]) => {
      if (TOP_LEVEL_FIELDS.has(k)) topLevel[k] = v;
      else details[k] = v;
    });
    // Coerce numerics
    ["rto_hours", "rpo_hours", "mtpd_hours", "staff_count", "minimum_staff", "seat_capacity", "power_kva", "cooling_tons"].forEach(k => {
      if (topLevel[k] !== undefined && topLevel[k] !== "") topLevel[k] = Number(topLevel[k]);
      if (details[k] !== undefined && details[k] !== "") details[k] = Number(details[k]);
    });
    // V22.9.8 — provide sensible defaults when fields aren't in this blueprint
    if (!topLevel.primary_location) topLevel.primary_location = "—";
    if (!topLevel.current_address_line1) topLevel.current_address_line1 = "—";
    setSaving(true);
    try {
      // Multi-role: submit one profile per asset_type (federation engine
      // dedupes by source_profile_id on each so they don't collide).
      const targets = template?.blueprint?.asset_types?.length ? template.blueprint.asset_types : [topLevel.asset_type || "other"];
      let primaryId = null;
      for (const at of targets) {
        const payload = { ...topLevel, asset_type: at, details };
        const res = await api.post("/profile-update/submit", payload);
        primaryId = primaryId || res.data?.id;
      }
      updateUser({ profile_completed: true });
      toast.success(targets.length > 1 ? `Profile saved across ${targets.length} asset types — welcome aboard!` : "Profile saved — welcome aboard!");
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(extractErrorMessage(e, "Failed to save profile"));
    } finally {
      setSaving(false);
    }
  };

  if (!template) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading your profile…</div>;
  }

  // ── Step bodies (render based on field list for the active step) ──
  const renderField = (field) => {
    switch (field) {
      case "asset_name": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Name *</Label>
          <Input value={form.asset_name || ""} onChange={e => set("asset_name", e.target.value)} placeholder="e.g. Payroll Process / Workday HCM / Core Networking" data-testid="profile-field-asset_name" /></div>
      );
      case "description": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Description</Label>
          <Textarea rows={3} value={form.description || ""} onChange={e => set("description", e.target.value)} placeholder="One-paragraph summary" data-testid="profile-field-description" /></div>
      );
      case "owner_name": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Owner Name *</Label>
          <Input value={form.owner_name || ""} onChange={e => set("owner_name", e.target.value)} data-testid="profile-field-owner_name" /></div>
      );
      case "owner_phone": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Owner Phone</Label>
          <Input value={form.owner_phone || ""} onChange={e => set("owner_phone", e.target.value)} placeholder="+1 555…" data-testid="profile-field-owner_phone" /></div>
      );
      case "primary_location": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Primary Location *</Label>
          <Input value={form.primary_location || ""} onChange={e => set("primary_location", e.target.value)} placeholder="HQ / Data Centre / Region" data-testid="profile-field-primary_location" /></div>
      );
      case "current_address_line1": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Address *</Label>
          <Input value={form.current_address_line1 || ""} onChange={e => set("current_address_line1", e.target.value)} data-testid="profile-field-address" /></div>
      );
      case "city": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">City</Label>
          <Input value={form.city || ""} onChange={e => set("city", e.target.value)} data-testid="profile-field-city" /></div>
      );
      case "country": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Country</Label>
          <Input value={form.country || ""} onChange={e => set("country", e.target.value)} data-testid="profile-field-country" /></div>
      );
      case "business_criticality": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Business Criticality</Label>
          <Select value={form.business_criticality || "medium"} onValueChange={(v) => set("business_criticality", v)}>
            <SelectTrigger data-testid="profile-field-criticality"><SelectValue /></SelectTrigger>
            <SelectContent>{["low", "medium", "high", "critical"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
      );
      case "tier": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Tier</Label>
          <Select value={form.tier || "tier_3"} onValueChange={(v) => set("tier", v)}>
            <SelectTrigger data-testid="profile-field-tier"><SelectValue /></SelectTrigger>
            <SelectContent>{["tier_1", "tier_2", "tier_3"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
      );
      case "rto_hours": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">RTO (hours)</Label>
          <Input type="number" value={form.rto_hours ?? ""} onChange={e => set("rto_hours", e.target.value)} data-testid="profile-field-rto_hours" /></div>
      );
      case "rpo_hours": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">RPO (hours)</Label>
          <Input type="number" value={form.rpo_hours ?? ""} onChange={e => set("rpo_hours", e.target.value)} data-testid="profile-field-rpo_hours" /></div>
      );
      case "mtpd_hours": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">MTPD (hours)</Label>
          <Input type="number" value={form.mtpd_hours ?? ""} onChange={e => set("mtpd_hours", e.target.value)} data-testid="profile-field-mtpd_hours" /></div>
      );
      case "staff_count": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Total Staff</Label>
          <Input type="number" value={form.staff_count ?? ""} onChange={e => set("staff_count", e.target.value)} data-testid="profile-field-staff_count" /></div>
      );
      case "minimum_staff": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Minimum Staff</Label>
          <Input type="number" value={form.minimum_staff ?? ""} onChange={e => set("minimum_staff", e.target.value)} data-testid="profile-field-minimum_staff" /></div>
      );
      case "seat_capacity": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Seat Capacity</Label>
          <Input type="number" value={form.seat_capacity ?? ""} onChange={e => set("seat_capacity", e.target.value)} data-testid="profile-field-seat_capacity" /></div>
      );
      case "facility_type": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Facility Type</Label>
          <Input value={form.facility_type || ""} onChange={e => set("facility_type", e.target.value)} placeholder="HQ / Branch / Data Centre" data-testid="profile-field-facility_type" /></div>
      );
      case "hosting_type": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Hosting Type</Label>
          <Select value={form.hosting_type || "on_prem"} onValueChange={(v) => set("hosting_type", v)}>
            <SelectTrigger data-testid="profile-field-hosting_type"><SelectValue /></SelectTrigger>
            <SelectContent>{["on_prem", "saas", "iaas", "paas", "hybrid"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
      );
      case "deployment_regions": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Deployment Regions (comma-separated)</Label>
          <Input value={(form.deployment_regions || []).join(", ")} onChange={setListCSV("deployment_regions")} placeholder="us-east-1, us-west-2, eu-west-1" data-testid="profile-field-deployment_regions" /></div>
      );
      case "data_classification": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Data Classification</Label>
          <Select value={form.data_classification || "internal"} onValueChange={(v) => set("data_classification", v)}>
            <SelectTrigger data-testid="profile-field-data_classification"><SelectValue /></SelectTrigger>
            <SelectContent>{["public", "internal", "confidential", "restricted"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
      );
      case "compliance_tags": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Compliance Tags (comma-separated)</Label>
          <Input value={(form.compliance_tags || []).join(", ")} onChange={setListCSV("compliance_tags")} placeholder="PCI-DSS, HIPAA, SOC2" data-testid="profile-field-compliance_tags" /></div>
      );
      case "it_function_scope": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">IT Function Scope</Label>
          <Select value={form.it_function_scope || "other"} onValueChange={(v) => set("it_function_scope", v)}>
            <SelectTrigger data-testid="profile-field-it_function_scope"><SelectValue /></SelectTrigger>
            <SelectContent>{["network", "database", "cloud", "security", "identity", "messaging", "erp_backbone", "other"].map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select></div>
      );
      case "vendor_id": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Hosting Vendor (if SaaS / IaaS)</Label>
          <AssetPicker api={api} type="vendor" value={form.vendor_id_label ? { name: form.vendor_id_label } : null}
            onChange={(v) => { set("vendor_id", v?.id || null); set("vendor_id_label", v?.name || ""); }} testIdSuffix="profile" /></div>
      );
      case "vendor_type": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Vendor Type</Label>
          <Input value={form.vendor_type || ""} onChange={e => set("vendor_type", e.target.value)} placeholder="SaaS / IaaS / MSP / Hardware" data-testid="profile-field-vendor_type" /></div>
      );
      case "vendor_contact_email": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Vendor Contact Email</Label>
          <Input type="email" value={form.vendor_contact_email || ""} onChange={e => set("vendor_contact_email", e.target.value)} data-testid="profile-field-vendor_contact_email" /></div>
      );
      case "sla_url": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">SLA Document URL</Label>
          <Input type="url" value={form.sla_url || ""} onChange={e => set("sla_url", e.target.value)} placeholder="https://intranet/sla/…" data-testid="profile-field-sla_url" />
          <p className="text-[10px] text-slate-400">V22.9.6 — saved into the Documents inventory as a vendor_sla shell.</p></div>
      );
      case "contract_url": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Master Contract URL</Label>
          <Input type="url" value={form.contract_url || ""} onChange={e => set("contract_url", e.target.value)} placeholder="https://intranet/contracts/…" data-testid="profile-field-contract_url" />
          <p className="text-[10px] text-slate-400">Saved into Documents as a vendor_contract shell.</p></div>
      );
      case "key_personnel": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Key Personnel (comma-separated names)</Label>
          <Input value={(form.key_personnel || []).join(", ")} onChange={setListCSV("key_personnel")} data-testid="profile-field-key_personnel" /></div>
      );
      case "upstream_asset_ids": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Upstream Dependencies (free-text, comma-separated for now)</Label>
          <Input value={(form.upstream_asset_ids || []).join(", ")} onChange={setListCSV("upstream_asset_ids")} placeholder="Active Directory, Core Network" data-testid="profile-field-upstream" /></div>
      );
      case "downstream_asset_ids": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Downstream Dependants (free-text, comma-separated)</Label>
          <Input value={(form.downstream_asset_ids || []).join(", ")} onChange={setListCSV("downstream_asset_ids")} placeholder="Claims Processing, FNOL" data-testid="profile-field-downstream" /></div>
      );
      /* ── V22.9.8 Asset Demography fields ──────────────────────────── */
      case "process_code": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Process ID / Code</Label>
          <Input value={form.process_code || ""} onChange={e => set("process_code", e.target.value)} placeholder="e.g. P-UW-001" data-testid="profile-field-process_code" /></div>
      );
      case "department": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Department</Label>
          <Input value={form.department || ""} onChange={e => set("department", e.target.value)} placeholder="Pre-filled from your user record" data-testid="profile-field-department" /></div>
      );
      case "service_lines": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Service Lines (comma-separated)</Label>
          <Input value={(form.service_lines || []).join(", ")} onChange={setListCSV("service_lines")} placeholder="Auto Insurance, Property Insurance" data-testid="profile-field-service_lines" /></div>
      );
      case "regulatory_scope": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Regulatory Scope <HelpTip term="regulatory_scope" glossary={glossary} /></Label>
          <Input value={(form.regulatory_scope || []).join(", ")} onChange={setListCSV("regulatory_scope")} placeholder="SOX, GDPR, HIPAA, PCI-DSS" data-testid="profile-field-regulatory_scope" /></div>
      );
      case "data_residency": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Primary Data Residency <HelpTip term="data_residency" glossary={glossary} /></Label>
          <Input value={form.data_residency || ""} onChange={e => set("data_residency", e.target.value)} placeholder="e.g. United States, EU, UK" data-testid="profile-field-data_residency" /></div>
      );
      case "app_id_code": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">App ID Code <HelpTip term="app_id_code" glossary={glossary} /></Label>
          <Input value={form.app_id_code || ""} onChange={e => set("app_id_code", e.target.value)} placeholder="e.g. APP-PAY-001" data-testid="profile-field-app_id_code" /></div>
      );
      case "acronym": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Acronym / Short Name</Label>
          <Input value={form.acronym || ""} onChange={e => set("acronym", e.target.value)} placeholder="e.g. PAY, CRM" data-testid="profile-field-acronym" /></div>
      );
      case "app_version": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Version</Label>
          <Input value={form.app_version || ""} onChange={e => set("app_version", e.target.value)} placeholder="e.g. 2.5.1" data-testid="profile-field-app_version" /></div>
      );
      case "app_type": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Application Type</Label>
          <Select value={form.app_type || "internal"} onValueChange={(v) => set("app_type", v)}>
            <SelectTrigger data-testid="profile-field-app_type"><SelectValue /></SelectTrigger>
            <SelectContent>{["internal", "customer_facing", "vendor_managed", "integration", "data_warehouse", "other"].map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
          </Select></div>
      );
      case "tech_stack": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Tech Stack <HelpTip term="tech_stack" glossary={glossary} /></Label>
          <Input value={form.tech_stack || ""} onChange={e => set("tech_stack", e.target.value)} placeholder="Java 17 · Spring Boot · PostgreSQL · React 18" data-testid="profile-field-tech_stack" /></div>
      );
      case "backup_owner_name": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Backup Owner Name</Label>
          <Input value={form.backup_owner_name || ""} onChange={e => set("backup_owner_name", e.target.value)} data-testid="profile-field-backup_owner_name" /></div>
      );
      case "backup_owner_email": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Backup Owner Email</Label>
          <Input type="email" value={form.backup_owner_email || ""} onChange={e => set("backup_owner_email", e.target.value)} data-testid="profile-field-backup_owner_email" /></div>
      );
      case "backup_owner_phone": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Backup Owner Phone</Label>
          <Input value={form.backup_owner_phone || ""} onChange={e => set("backup_owner_phone", e.target.value)} data-testid="profile-field-backup_owner_phone" /></div>
      );
      case "exec_sponsor_name": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Executive Sponsor Name</Label>
          <Input value={form.exec_sponsor_name || ""} onChange={e => set("exec_sponsor_name", e.target.value)} data-testid="profile-field-exec_sponsor_name" /></div>
      );
      case "exec_sponsor_email": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Executive Sponsor Email</Label>
          <Input type="email" value={form.exec_sponsor_email || ""} onChange={e => set("exec_sponsor_email", e.target.value)} data-testid="profile-field-exec_sponsor_email" /></div>
      );
      case "owner_email": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Owner Email</Label>
          <Input type="email" value={form.owner_email || ""} onChange={e => set("owner_email", e.target.value)} data-testid="profile-field-owner_email" /></div>
      );
      case "hosting_provider": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Hosting Provider</Label>
          <Input value={form.hosting_provider || ""} onChange={e => set("hosting_provider", e.target.value)} placeholder="AWS / Azure / GCP / On-prem DC name" data-testid="profile-field-hosting_provider" /></div>
      );
      case "vlan_id": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">VLAN ID <HelpTip term="vlan_id" glossary={glossary} /></Label>
          <Input value={form.vlan_id || ""} onChange={e => set("vlan_id", e.target.value)} placeholder="e.g. 100, 200, 4001" data-testid="profile-field-vlan_id" /></div>
      );
      case "load_balancer_type": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Load Balancer <HelpTip term="load_balancer_type" glossary={glossary} /></Label>
          <Input value={form.load_balancer_type || ""} onChange={e => set("load_balancer_type", e.target.value)} placeholder="F5 BIG-IP · AWS ALB · NGINX" data-testid="profile-field-load_balancer_type" /></div>
      );
      case "dns_names": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">DNS Names <HelpTip term="dns_names" glossary={glossary} /></Label>
          <Input value={(form.dns_names || []).join(", ")} onChange={setListCSV("dns_names")} placeholder="payroll.acme.com, payroll-uat.acme.com" data-testid="profile-field-dns_names" /></div>
      );
      case "firewall_zones": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Firewall Zones <HelpTip term="firewall_zones" glossary={glossary} /></Label>
          <Input value={(form.firewall_zones || []).join(", ")} onChange={setListCSV("firewall_zones")} placeholder="DMZ, INTERNAL, PCI" data-testid="profile-field-firewall_zones" /></div>
      );
      case "connectivity_rows":
        return (
          <ConnectivityRowsEditor
            key={field}
            rows={form.connectivity_rows || []}
            onChange={(rows) => set("connectivity_rows", rows)}
            glossary={glossary}
          />
        );
      case "facility_address": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs">Facility Address (full)</Label>
          <Textarea rows={2} value={form.facility_address || ""} onChange={e => set("facility_address", e.target.value)} placeholder="Street, City, State, Zip, Country" data-testid="profile-field-facility_address" /></div>
      );
      case "site_tier": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Site Tier <HelpTip term="site_tier" glossary={glossary} /></Label>
          <Select value={form.site_tier || "tier_3_dc"} onValueChange={(v) => set("site_tier", v)}>
            <SelectTrigger data-testid="profile-field-site_tier"><SelectValue /></SelectTrigger>
            <SelectContent>{["tier_1_dc", "tier_2_dc", "tier_3_dc", "tier_4_dc", "edge_site", "office"].map(v => <SelectItem key={v} value={v}>{v.replace(/_/g, " ").toUpperCase()}</SelectItem>)}</SelectContent>
          </Select></div>
      );
      case "power_kva": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Power (kVA) <HelpTip term="power_kva" glossary={glossary} /></Label>
          <Input type="number" value={form.power_kva ?? ""} onChange={e => set("power_kva", e.target.value)} data-testid="profile-field-power_kva" /></div>
      );
      case "cooling_tons": return (
        <div key={field} className="space-y-1.5"><Label className="text-xs flex items-center gap-1">Cooling (tons) <HelpTip term="cooling_tons" glossary={glossary} /></Label>
          <Input type="number" value={form.cooling_tons ?? ""} onChange={e => set("cooling_tons", e.target.value)} data-testid="profile-field-cooling_tons" /></div>
      );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-violet-50 py-10 px-4" data-testid="asset-profile-gate">
      <div className="max-w-3xl mx-auto mb-5">
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <Badge variant="outline" className="text-violet-700 border-violet-200 bg-violet-50 text-[10px]" data-testid="profile-gate-version-badge">V22.9.8 NEW · Asset Demography</Badge>
          <Badge variant="outline" className="text-slate-500 border-slate-200 text-[10px]">Mandatory · First Login</Badge>
          {template.multi_role && (
            <Badge className="bg-amber-100 text-amber-800 border-amber-300 text-[10px]" data-testid="profile-gate-multi-role-badge">
              Combined session — {(template.profile_roles || []).length} roles
            </Badge>
          )}
        </div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          {template.blueprint?.title}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{template.blueprint?.subtitle}</p>
        {template.multi_role && (
          <div className="mt-3 p-2.5 bg-amber-50 border border-amber-200 rounded text-[12px] text-amber-900" data-testid="profile-gate-multi-role-banner">
            <strong>Multi-role users:</strong> Shared steps (Identity, Compliance, Criticality) are filled once and reused across all your profiles. Federation creates one row per asset_type on submission.
          </div>
        )}
      </div>

      <StepDialog
        open
        onOpenChange={() => { /* gate cannot be dismissed */ }}
        title={template.blueprint?.title}
        steps={stepPills}
        currentStep={step}
        onStepChange={setStep}
        onSubmit={handleSubmit}
        submitLabel="Save Profile & Continue"
        saving={saving}
        canAdvance={canAdvance}
        maxWidth="max-w-3xl"
        dismissible={false}
      >
        <div className="grid md:grid-cols-3 gap-5" data-testid={`asset-profile-step-${step}`}>
          <div className="space-y-4 md:col-span-2">
            {currentStepDef?.fields?.map(renderField)}
          </div>
          <div className="md:col-span-1">
            <AssetProfileCopilot
              api={api}
              form={form}
              blueprint={template.blueprint}
              isFinalStep={step === stepsConfig.length - 1}
            />
          </div>
        </div>
      </StepDialog>
    </div>
  );
}
